import {useCallback, useEffect, useState} from 'react';

import {getAlertContentAvailableOptions} from 'src/openapi/admin/apis/AlertAvailableOptionsApi';
import {SearchListType} from 'src/openapi/admin/models/SearchListType';
import {useAppDispatch} from 'src/store/hooks';

import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';

type Props = {
    userId: number,
};

export const useGetUserAlertContentAvailableOptions = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();
    const [availableOption, setAvailableOption] = useState<Array<DictionaryItem>>([]);
    const [error, setError] = useState<Error | null>(null);

    const loadAvailableOptions = useCallback(async () => {
        const response = await apiDispatch(getAlertContentAvailableOptions({userId}));
        if (response?.payload.success) {
            setAvailableOption(response.payload.data || []);
        } else {
            setAvailableOption([]);
            setError(new Error('Failed to load available options'));
        }
    }, [apiDispatch, userId]);

    useEffect(() => {
        loadAvailableOptions();
    }, [loadAvailableOptions]);

    const getSearchTypeDescription = (type: string): string => {
        switch (type) {
            case SearchListType.COMPANY:
                return 'Company';
            case SearchListType.INVESTOR:
                return 'Investor';
            case SearchListType.SP:
                return 'Service Provider';
            case SearchListType.LP:
                return 'Limited Partner';
            case SearchListType.PERSON:
                return 'Person';
            case SearchListType.DEBT:
                return 'Debt';
            default:
                return type;
        }
    };

    const getAvailableOptionDescriptionByCode = useCallback((code: string) => {
        if (error) {
            return getSearchTypeDescription(code);
        }
        return availableOption?.find(e => e.code === code)?.description || code;
    }, [availableOption, error]);

    return {availableOption, getAvailableOptionsByCode: getAvailableOptionDescriptionByCode};
};
